import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AppShell from '../components/work/AppShell';
import Peek from '../components/work/Peek';
import WorkKanban from '../components/work/WorkKanban';
import {
  columnItemsForState,
  neighboursForDrop,
  orderedItemKeys,
  previousAfter,
  previousBefore,
  sortItemsByColumn,
} from '../components/work/kanban-rank';
import { BrandEmpty, LoadingRows, PageTitle, WorkPageError } from '../components/work/page-states';
import ShowSubtasksCheckbox from '../components/work/ShowSubtasksCheckbox';
import { useOverlays } from '../components/work/overlay-context';
import { ApiError, apiFetch, v1Path } from '../lib/api';
import { filtersToSearchParams } from '../lib/filters';
import { isProjectId } from '../lib/project-path';
import type { StateDto, WorkItemFull, WorkItemSlim } from '../lib/work-types';

const PAGE = 100;

type ListResponse = {
  items: WorkItemSlim[];
  page: { limit: number; offset: number; total: number };
};

type MoveBody = {
  version: number;
  state_id: string;
  before_id?: string;
  after_id?: string;
};

function sortStatesByPosition(rows: StateDto[]): StateDto[] {
  return [...rows].sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
}

async function listParentChildren(
  projectId: string,
  parentId: string,
  showNested: boolean,
): Promise<WorkItemSlim[]> {
  const collected: WorkItemSlim[] = [];
  let offset = 0;
  for (;;) {
    const params = filtersToSearchParams(
      showNested ? { ancestor_id: parentId } : { parent_ids: [parentId] },
      { show_sub_items: true, order_by: 'sort_order' },
    );
    params.set('limit', String(PAGE));
    params.set('offset', String(offset));
    const res = await apiFetch<ListResponse>(
      `${v1Path(`/projects/${projectId}/work-items`)}?${params.toString()}`,
    );
    collected.push(...res.items);
    if (collected.length >= res.page.total || res.items.length === 0) return collected;
    offset += PAGE;
  }
}

export default function ParentSubtaskBoard() {
  const { projectId, workItemId } = useParams<{ projectId: string; workItemId: string }>();
  const { toast } = useOverlays();
  const [parent, setParent] = useState<WorkItemFull | null>(null);
  const [items, setItems] = useState<WorkItemSlim[]>([]);
  const [states, setStates] = useState<StateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNested, setShowNested] = useState(true);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!projectId || !workItemId) return;
      if (!opts?.silent) setLoading(true);
      setError(null);
      try {
        const [full, children, stateRes] = await Promise.all([
          apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${workItemId}`)),
          listParentChildren(projectId, workItemId, showNested),
          apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)),
        ]);
        setParent(full);
        setItems(children);
        setStates(stateRes.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load board');
        setParent(null);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [projectId, workItemId, showNested],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onChanged() {
      void load({ silent: true });
    }
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  const columns = useMemo(() => sortStatesByPosition(states), [states]);
  const peekKeys = useMemo(() => orderedItemKeys(columns, items), [columns, items]);

  async function commitMove(item: WorkItemSlim, destStateId: string, insertBeforeId: string | null) {
    if (!projectId) return;
    if (item.id === insertBeforeId && item.state_id === destStateId) return;

    const destColumn = columnItemsForState(items, destStateId);
    const neighbours = neighboursForDrop(destColumn, item.id, insertBeforeId);
    const samePlace =
      item.state_id === destStateId &&
      (neighbours.before_id ?? null) === previousBefore(destColumn, item.id) &&
      (neighbours.after_id ?? null) === previousAfter(destColumn, item.id);
    if (samePlace) return;

    const snapshot = items;
    const optimistic: WorkItemSlim = { ...item, state_id: destStateId };
    setItems((curr) => curr.map((row) => (row.id === item.id ? optimistic : row)));

    const body: MoveBody = {
      version: item.version,
      state_id: destStateId,
      ...neighbours,
    };

    try {
      const dto = await apiFetch<WorkItemSlim>(
        v1Path(`/projects/${projectId}/work-items/${item.id}/move`),
        { method: 'POST', body: JSON.stringify(body) },
      );
      setItems((curr) =>
        sortItemsByColumn(curr.map((row) => (row.id === dto.id ? { ...row, ...dto } : row))),
      );
      await load({ silent: true });
    } catch (err) {
      setItems(snapshot);
      if (err instanceof ApiError && err.status === 409) {
        toast('Someone else updated this card. Reloading.');
        await load({ silent: true });
        return;
      }
      toast(err instanceof Error ? err.message : 'Move failed');
    }
  }

  if (!projectId || !workItemId || !isProjectId(projectId)) {
    return <div className="p-6 text-sm text-muted-foreground">Missing work item.</div>;
  }

  return (
    <AppShell projectId={projectId}>
      <div className="flex flex-1 flex-col gap-4">
        {parent ? (
          <div>
            <Link
              to={`/projects/${projectId}/work-items/${parent.id}`}
              className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              {parent.key}
            </Link>
            <PageTitle
              title={parent.title}
              count={loading || error ? undefined : items.length}
              action={
                <ShowSubtasksCheckbox
                  id="show-nested"
                  label="Show nested"
                  checked={showNested}
                  onCheckedChange={setShowNested}
                />
              }
            />
          </div>
        ) : null}

        {loading ? <LoadingRows /> : null}
        <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />

        {!loading && !error && parent && items.length === 0 ? (
          <BrandEmpty title="No subtasks." />
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <WorkKanban
            projectId={projectId}
            columns={columns}
            items={items}
            showEmptyGroups
            canReorder
            onMove={(item, destStateId, insertBeforeId) => {
              void commitMove(item, destStateId, insertBeforeId);
            }}
          />
        ) : null}

        <Peek projectId={projectId} itemKeys={peekKeys} />
      </div>
    </AppShell>
  );
}
